import { useTranslation } from 'react-i18next';
import { FunctionComponent, useEffect, useState } from 'react';
import { Button, Card, Flex, InputNumber, Modal, Skeleton, Table, TableProps } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

// components
import NoData from 'components/noData/noData';
import StatusComponent from 'components/status/status';
import PageTitle from 'components/title/title';
import CustomCard from 'components/customCard/customCard';
import Riyal from 'components/riyal/riyal';
import PriceElement from 'components/price/price';
import TableTotal from 'components/tableTotal/tableTotal';
import NumberElement from 'components/number/number';

// models
import { Package } from '../models/wallet';

// services
import { Payment, PaymentsPayload } from 'modules/main/companyInformation/payments/models/payment';
import { onExportPaymentInvoice, onGetPayments } from 'modules/main/companyInformation/payments/services/paymentsService';
import NotificationService from 'core/services/notification';
import { onGetPackages, onGetPaymentLink } from '../services/walletServices';
import { isRTL } from 'core/services/language';
import { StatusColors } from 'utils/statusColors';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import ListButton from 'components/listButton/listButton';
import { downloadFile } from 'utils/downloadFile';
import { loadScript } from 'utils/loadScript';
import OrderStatusComponent from '../components/orderStatus';
import { getUserFromToken } from 'core/services/auth';
import { useDispatch } from 'react-redux';
import { setAdmin } from 'stores/useAdminStore';
import { setCompany } from 'stores/useCompanyStore';

const WalletPage: FunctionComponent = () => {
	const { t } = useTranslation();
	const location = useLocation();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const [packages, setPackages] = useState<Package[]>([]);
	const [pointValue, setPointValue] = useState<number>(0);
	const [transactions, setTransactions] = useState<Payment[]>([]);
	const [customAmount, setCustomAmount] = useState<number | null>(0);

	const [paymentState, setPaymentState] = useState<'payment' | 'checking'>('payment');
	const [merchantId, setMerchantId] = useState<string>('');

	const [paymentModalOpen, setPaymentModalOpen] = useState<boolean>(false);
	const [paymentLoading, setPaymentLoading] = useState<boolean>(false);

	const [packagesLoading, setPackagesLoading] = useState<boolean>(true);
	const [transactionsLoading, setTransactionsLoading] = useState<boolean>(true);

	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);

	const columns: TableProps['columns'] = [
		{
			title: t('COMMON.ID'),
			dataIndex: 'id',
			key: 'id',
		},
		{
			title: t('WALLET.PACKAGE'),
			dataIndex: 'translated_pay_for',
			key: 'translated_pay_for',
			render: (pay_for) => <span>{isRTL() ? pay_for.ar : pay_for.en}</span>,
		},
		{
			title: t('COMMON.AMOUNT'),
			dataIndex: 'amount',
			key: 'amount',
			render: (amount, record) =>
				record?.currency === 'SAR' ? (
					<PriceElement value={amount.total} decimalScale={2} color="#333" />
				) : (
					<span>
						{record?.currency} <NumberElement value={amount.total} decimalScale={2} />
					</span>
				),
		},
		{
			title: t('COMMON.POINTS'),
			dataIndex: 'points',
			key: 'points',
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'status',
			key: 'status',
			render: (status) => <StatusComponent text={t(`COMMON.${status.toUpperCase()}`)} color={StatusColors[status]} />,
		},
		{
			title: t('COMMON.CREATED_AT'),
			dataIndex: 'created_at',
			key: 'created_at',
			render: (date) => dayjs(date).format('DD/MM/YYYY, hh:mm A'),
		},
		{
			title: t('WALLET.INVOICE_NUMBER'),
			dataIndex: 'reference',
			key: 'reference',
		},
		{
			title: t('WALLET.GATEWAY'),
			dataIndex: 'translated_gateway',
			key: 'translated_gateway',
			render: (gateway) => <span>{isRTL() ? gateway.ar : gateway.en}</span>,
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: 'actions',
			key: 'actions',
			render: (_, record) => {
				return record?.status == 'Completed' ? <ListButton type="download" onClick={() => downloadInvoice(record.id)} /> : null;
			},
		},
	];

	useEffect(() => {
		setCurrentPage(getCurrentPage());

		getPackages();
		getTransactionLogs();
	}, []);

	const getCurrentPage = () => {
		const params = new URLSearchParams(location.search);
		const page = params.get('page');
		return page ? parseInt(page) : 1;
	};

	const getTransactionLogs = async () => {
		try {
			setTransactionsLoading(true);

			const payload: PaymentsPayload = {
				page: getCurrentPage(),
				payment_type: 'PointWalletCharging',
			};

			const response = await onGetPayments(payload);

			setTransactions(response.payment_invoices);
			setTotalRecords(response.pagination.total);

			setTransactionsLoading(false);
		} catch (error) {
			setTransactionsLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			const params = new URLSearchParams(location.search);
			params.set('page', page.toString());
			navigate(`${location.pathname}?${params.toString()}`, { replace: true });

			setTransactionsLoading(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });

			const payload: PaymentsPayload = {
				page: page,
				payment_type: 'PointWalletCharging',
			};

			const response = await onGetPayments(payload);
			setTransactions(response['payment_invoices']);

			setCurrentPage(page);
			setTransactionsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setTransactionsLoading(false);
		}
	};

	const getPackages = async () => {
		try {
			const response = await onGetPackages();
			setPackages(response.packages);
			setPointValue(response.point_price_in_sar);
			setPackagesLoading(false);
		} catch (error) {
			setPackagesLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const cancelPaymentHandler = () => {
		setPaymentModalOpen(false);
		setPaymentLoading(false);
	};

	const onBuyHandler = async (type: 'point_package_id' | 'custom_amount', value: number) => {
		try {
			setPaymentLoading(true);
			setPaymentModalOpen(true);

			const response = await onGetPaymentLink(type, value);

			loadScript(response.payment_url)
				.then(() => {
					(window as any).ProcessCheckoutPayment({
						Checkout_Method: 3,
						Frame_Id: 'payment_frame',
						SecureVerification_Method: 1,
						Call_Back: ResponseCallBack,
					});
				})
				.catch((error) => {
					console.error('Error loading script:', error);
				});

			setPaymentLoading(false);
		} catch (error) {
			setPaymentLoading(false);
			setPaymentModalOpen(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.SUBMIT_ERROR'));
		}
	};

	const downloadInvoice = async (id: number) => {
		try {
			const exportResponse = await onExportPaymentInvoice(id);

			if (exportResponse === null) {
				NotificationService.error(t('COMMON.ERROR'), t('COMMON.DOWNLOAD_ERROR'));
				return;
			}

			await downloadFile(exportResponse.download_url, exportResponse.filename || 'invoice.pdf');
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.DOWNLOAD_ERROR'));
		}
	};

	const ResponseCallBack = (response: { merchantReference: string; orderId: string }) => {
		setMerchantId(response.merchantReference);
		setPaymentState('checking');
	};

	const refetchAccountHandler = async () => {
		const response = await getUserFromToken();
		dispatch(setAdmin(response.company_user));
		dispatch(setCompany(response.company_user.company));
	};

	return (
		<>
			<PageTitle name={t('TITLE.WALLET')} />

			<Flex gap={20} justify="space-around" wrap className="mt-4">
				{packagesLoading ? (
					[1, 2, 3, 4].map((_, index) => (
						<div style={{ minWidth: 200, maxWidth: 350, flex: 1 }} key={index}>
							<Skeleton.Node active className="fullWidth" style={{ minHeight: 308, minWidth: '100%' }} />
						</div>
					))
				) : (
					<>
						{packages.map((packageData, index) => (
							<div key={index} style={{ minWidth: 200, maxWidth: 350, flex: 1 }}>
								<Card className=" h-100 hovered">
									<Flex vertical justify="space-between" align="center" className="h-100">
										<h5 className="fw-medium mb-4">{t(isRTL() ? packageData.translated_title.ar : packageData.translated_title.en)}</h5>

										<div>
											<PriceElement
												value={+packageData.price}
												decimalScale={2}
												fontSize="calc(1.325rem + .9vw)"
												fontStyle={{ fontWeight: 500 }}
												riyalSize={25}
											/>
										</div>

										<div className="my-2 text-center">
											<div className="cancelled d-inline-block">
												<PriceElement value={+packageData.original_price} color="red" fontSize={18} riyalSize={14} />
											</div>
											<h4>
												{packageData.points} {t('COMMON.POINTS')}
											</h4>
										</div>

										<Button type="primary" className="mt-4 px-4" onClick={() => onBuyHandler('point_package_id', packageData.id)}>
											{t('WALLET.BUY_NOW')}
										</Button>
									</Flex>
								</Card>
							</div>
						))}

						<div style={{ minWidth: 200, maxWidth: 350, flex: 1 }}>
							<Card className="h-100 hovered">
								<Flex vertical justify="space-between" align="center" className="h-100">
									<h5 className="fw-medium mb-4">{t('WALLET.BUY_CUSTOM_POINTS')}</h5>

									<div>
										<InputNumber min={0} style={{ maxWidth: 170 }} addonAfter={<Riyal size={16} />} onChange={(value) => setCustomAmount(value)} />
										<div className="mt-2 fw-medium d-flex align-items-center gap-1 ">
											{t('COMMON.POINT')} = <PriceElement value={pointValue} decimalScale={2} fontSize={18} riyalSize={12} />
										</div>
										{!!customAmount && pointValue > 0 && (
											<h4 className="mt-2 text-center">
												<NumberElement value={Math.floor(customAmount / pointValue)} style={{ display: 'inline-block' }} /> {t('COMMON.POINTS')}
											</h4>
										)}
									</div>

									<Button
										type="primary"
										className="mt-4 px-4"
										onClick={() => onBuyHandler('custom_amount', customAmount || 0)}
										disabled={!customAmount}>
										{t('WALLET.BUY_NOW')}
									</Button>
								</Flex>
							</Card>
						</div>
					</>
				)}
			</Flex>

			<CustomCard title={t('WALLET.TRANSACTIONS')} className="mt-5">
				<Table
					columns={columns}
					dataSource={transactions}
					scroll={{ x: 600 }}
					rowKey="id"
					loading={transactionsLoading}
					pagination={{
						pageSize: 25,
						total: totalRecords,
						showSizeChanger: false,
						current: currentPage,
						onChange: fetchMoreData,
						showTotal: (total) => <TableTotal total={total} />,
					}}
					locale={{ emptyText: <NoData /> }}
				/>
			</CustomCard>

			<Modal open={paymentModalOpen} onCancel={cancelPaymentHandler} title={null} footer={null} width="fit-content" centered>
				<Flex justify="center" align="center" style={{ width: 600, minHeight: 500, transition: 'width 0.5s ease', marginTop: 30 }}>
					{paymentState === 'payment' &&
						(paymentLoading ? <LoadingComponent /> : <iframe id="payment_frame" style={{ border: 'none', width: '100%', height: 500 }} />)}

					{paymentState === 'checking' && (
						<OrderStatusComponent
							merchantId={merchantId}
							onStatusChange={() => {
								getTransactionLogs();
								refetchAccountHandler();
								setTimeout(() => {
									setPaymentModalOpen(false);
									setPaymentState('payment');
								}, 3000);
							}}
						/>
					)}
				</Flex>
			</Modal>
		</>
	);
};

export default WalletPage;
